import { useEffect, useRef, useState } from "react";
import "../styles/product-image-lightbox.css";

export default function ProductImageLightbox({
  images = [],
  initialIndex = 0,
  open,
  onClose,
}) {
  const [index, setIndex] = useState(initialIndex);

  const [zoom, setZoom] = useState(1);

  const [pan, setPan] = useState({
    x: 0,
    y: 0,
  });

  const [dragging, setDragging] = useState(false);

  const dragStart = useRef({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      resetTransform();
    }
  }, [open, initialIndex]);

  // prevent background scrolling
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  // keyboard controls
  useEffect(() => {
    const handler = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        onClose();
      }

      if (e.key === "ArrowRight") {
        next();
      }

      if (e.key === "ArrowLeft") {
        previous();
      }
    };

    window.addEventListener("keydown", handler);

    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [open, index, images.length]);

  if (!open) return null;

  function resetTransform() {
    setZoom(1);

    setPan({
      x: 0,
      y: 0,
    });
  }

  function next() {
    if (images.length <= 1) return;

    setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

    resetTransform();
  }

  function previous() {
    if (images.length <= 1) return;

    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

    resetTransform();
  }

  function zoomIn() {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  }

  function zoomOut() {
    setZoom((prev) => Math.max(prev - 0.25, 1));
  }

  function wheelZoom(e) {
    e.preventDefault();

    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  }

  function toggleZoom() {
    setZoom((prev) => (prev === 1 ? 2.5 : 1));

    if (zoom === 1) {
      setPan({
        x: 0,
        y: 0,
      });
    }
  }

  function startDrag(e) {
    if (zoom <= 1) return;

    setDragging(true);

    dragStart.current = {
      x: e.clientX - pan.x,
      y: e.clientY - pan.y,
    };
  }

  function moveDrag(e) {
    if (!dragging) return;

    setPan({
      x: e.clientX - dragStart.current.x,

      y: e.clientY - dragStart.current.y,
    });
  }

  function stopDrag() {
    setDragging(false);
  }

  return (
    <div className="pil-overlay" onClick={onClose}>
      <button
        className="pil-close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>

      <div className="pil-image-container" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 && (
          <button
            className="pil-prev"
            onClick={previous}
            aria-label="Previous image"
          >
            ‹
          </button>
        )}

        <img
          src={images[index]}
          alt="Product zoom"
          className="pil-image"
          style={{
            transform: `
            translate(${pan.x}px,${pan.y}px)
            scale(${zoom})
            `,

            cursor: zoom > 1 ? (dragging ? "grabbing" : "grab") : "zoom-in",
          }}
          onClick={toggleZoom}
          onWheel={wheelZoom}
          onMouseDown={startDrag}
          onMouseMove={moveDrag}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        />

        {images.length > 1 && (
          <button className="pil-next" onClick={next} aria-label="Next image">
            ›
          </button>
        )}
      </div>

      <div className="pil-toolbar" onClick={(e) => e.stopPropagation()}>
        <button onClick={zoomOut}>−</button>

        <span>{Math.round(zoom * 100)}%</span>

        <button onClick={zoomIn}>+</button>
      </div>

      <div className="pil-counter">
        {index + 1} / {images.length}
      </div>
    </div>
  );
}
